import {defineStore} from "pinia";
import {PaymentProcessorsService} from "@/javascript/services/paymentProcessors.service";
import {loadStripe} from "@stripe/stripe-js";

export const usePaymentProcessorStore = defineStore('paymentProcessor', {

  state: () => (
    {
      // paymentProcessorService: new PaymentProcessorsService(),
      paymentProcessorService: null,
      paymentProcessors: [],
      activeParentProcessor: null,
      organizationId: null,
      loading: true,
      // these are for analytic events
      traceableType: "PaymentProcessor",
      traceableName: "payment-processor",

      processorConfig: {
        loading: false,
        success: false,
        error: false,
        message: "",
        loadingMessage: ""
      },
      editModal: {
        loading: false,
        success: false,
      },
    }
  ),

  actions: {
    setOrganizationId(id) {
      if (id) {
        this.organizationId = id;
        this.paymentProcessorService = new PaymentProcessorsService(id);
      } else {
        throw `Organization ID is not set: ${id}`;
      }
    },

    getOrgPaymentProcessors() {
      this.loading = true;
      this.paymentProcessors = [];
      this.activeParentProcessor = null;

      return this.paymentProcessorService.index()
        .then((res) => {
          if (res && res.data && res.data[0]) {
            this.paymentProcessors = res.data;
          }
          return true;
        })
        .then(() => {
          const hasActiveProcessor = this.paymentProcessors.some(processor => processor.active === true);
          // Get the payment processors from the organization's ancestor.
          // If the organization doesn't have Stripe/ Helcim set up, this is the processor that will get used

          if(!hasActiveProcessor) {
            return this.paymentProcessorService.activePaymentProcessor().then((result) => {
              const activeProcessor = result?.record;
              if(activeProcessor) {
                activeProcessor.viewOnly = true;
                this.activeParentProcessor = activeProcessor;
              }
              this.loading = false;
              return true;
            });
          } else {
            this.loading = false;
            return true;
          }
        });
    },

    configureStripe(key, secret) {
      this.processorConfig.message = "";
      this.processorConfig.loading = true;
      this.processorConfig.loadingMessage = "Authenticating your account ...";

      return this.testStripePubKey(key).then((isValidKey) => {
        if (isValidKey) {
          return this.paymentProcessorService.configureStripeAccount({
            api_key: key.trim(),
            api_secret: secret.trim(),
            org_id: this.organizationId
          });
        } else {
          this.processorConfig.loading = false;
          this.processorConfig.error = true;
          this.processorConfig.message = "Please check your secret key is correct";
          this.processorConfig.loadingMessage = "Linking Your Stripe Account ...";
          reject(new Error("Inval id public key"));
        }
      })
        .then((configResult) => {
          this.processorConfig.loading = false;
          if (configResult) {
            if (configResult.status === 200) {
              this.processorConfig.success = true;
              this.getOrgPaymentProcessors();
            } else {
              this.processorConfig.error = true;
              this.processorConfig.message = "Please check your secret key is correct";
              reject(new Error("Invalid secret key"));
            }
          } else {
            reject(new Error("Something went wrong"));
          }
          return true;
        });
    },

    configureHelcim(key) {
      this.processorConfig.message = "";
      this.processorConfig.loading = true;
      this.processorConfig.loadingMessage = "Linking your account ...";


      return this.paymentProcessorService.configureHelcimAccount(
        {
          api_key: key.trim(),
          org_id: this.organizationId
        }
      ).then((res) => {
        this.processorConfig.loading = false;
        if (res) {
          if (res.status === 200) {
            this.processorConfig.success = true;
            this.getOrgPaymentProcessors();
          } else {
            this.processorConfig.error = true;
            this.processorConfig.message = "Please check your secret key is correct";
          }
        } else {
          this.processorConfig.message = "Something went wrong linking your account";
          this.processorConfig.error = true;
        }
        return true;
      });
    },

    // Resets the modal data
    resetConfig() {
      this.processorConfig.error = false;
      this.processorConfig.message = "";
      this.processorConfig.success = false;

      this.editModal.loading = false;
      this.editModal.success = false;
    },

    updateProcessor(params) {
      this.editModal.loading = true;
      let data = {};
      if (params.type.includes("Helcim")) {
        data["processor_helcim_processor"] = params;
      }
      if (params.type.includes("Stripe")) {
        data["processor_stripe_processor"] = params;
      }
      return this.paymentProcessorService.update(params.id, data)
        .then(() => {
          this.getOrgPaymentProcessors();
          this.editModal.loading = false;
          this.editModal.success = true;
          return true;
        }).catch(() => {
          this.editModal.loading = false;
          return true;
        });
    },

    // A bit of a hack to test the Stripe publishable key is valid
    // No direct API methods to test this atm
    testStripePubKey(key) {
      return loadStripe(key).then((stripe) => {
        if (!stripe) {
          return false;
        } else {
          return stripe.createToken('pii', {personal_id_number: 'test'})
            .then((tokenResult) => {
              return tokenResult && tokenResult.hasOwnProperty('token');
            });
        }
      }).catch(() => {
        return false;
      });
    }
  },

  getters: {
    getProcessorByType: state => (type) => {
      return state.paymentProcessors.find(p => p.type.toLowerCase().includes(type.toLowerCase()));
    },
  }
});
